import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { DEV_BACKEND_BASE_URL } from '../config/devBackendUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || DEV_BACKEND_BASE_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_COPY = {
  family: {
    title: 'Family Archive',
    price: 'Founding one-time purchase',
    description:
      'Unlock a founding Family Archive for one tree. Legacy Archive upgrade remains available for eligible archives.',
    upgradeNote: 'Upgrade path: Free → Family Archive → Legacy Archive',
    cta: 'Activate Family Archive',
  },
  premium: {
    title: 'Legacy Archive',
    price: 'Founding upgrade',
    description:
      'Expand your founding Family Archive to Legacy Archive on an eligible tree.',
    upgradeNote: 'Requires an active Family Archive on the selected tree.',
    cta: 'Activate Legacy Archive',
  },
  legacy_book: {
    title: 'Legacy Book',
    price: '£49 founding offer',
    description:
      'Create and download the finished Legacy Book PDF for one archive. One-time payment — not a monthly subscription.',
    upgradeNote:
      'Applies to the archive you select. Family Archive subscription tools are coming later.',
    cta: 'Continue to checkout',
  },
};

function normalizePlanType(planParam) {
  if (planParam === 'premium') return 'premium';
  if (planParam === 'legacy_book' || planParam === 'legacy-book') return 'legacy_book';
  return 'family';
}

const BillingCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [trees, setTrees] = useState([]);
  const [billingState, setBillingState] = useState(null);
  const [selectedTreeId, setSelectedTreeId] = useState('');
  const [busy, setBusy] = useState(false);

  const planType = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizePlanType(params.get('plan'));
  }, [location.search]);

  const planCopy = PLAN_COPY[planType] || PLAN_COPY.family;

  useEffect(() => {
    if (!loading && !user) {
      navigate(`/register?plan=${planType}`, { replace: true });
    }
  }, [loading, user, navigate, planType]);

  useEffect(() => {
    if (!user) return;
    const run = async () => {
      try {
        const [treesRes, billingRes] = await Promise.all([
          axios.get(`${API}/trees`),
          axios.get(`${API}/billing/me`),
        ]);
        setTrees(treesRes.data || []);
        setBillingState(billingRes.data);
        if ((treesRes.data || []).length > 0) {
          setSelectedTreeId((treesRes.data || [])[0].id);
        }
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Unable to load checkout details');
      }
    };
    run();
  }, [user]);

  const needsTree = planType === 'family' || planType === 'legacy_book';

  const canStartCheckout = useMemo(() => {
    if (!billingState) return false;
    if (planType === 'legacy_book') {
      return Boolean(billingState.legacy_book_checkout_enabled);
    }
    return Boolean(billingState.can_upgrade_to?.includes(planType));
  }, [billingState, planType]);

  const startCheckout = async () => {
    if (!canStartCheckout) return;
    if (needsTree && !selectedTreeId) {
      toast.error('Choose an archive tree first');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout/start`, {
        plan_type: planType,
        tree_id: needsTree ? selectedTreeId : undefined,
      });
      if (data.provider === 'stripe' && data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      window.location.assign(data.checkout_url);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to start checkout');
    } finally {
      setBusy(false);
    }
  };

  const blockedReason = !billingState
    ? ''
    : !canStartCheckout
      ? planType === 'legacy_book'
        ? 'Legacy Book checkout is not available yet. Contact support to reserve founding access.'
        : planType === 'premium'
          ? 'Legacy Archive requires an active Family Archive first.'
          : 'This upgrade path is not available for your current plan.'
      : '';

  return (
    <div className="min-h-screen bg-[#faf8f5] text-[#1c1917]">
      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <Link to="/pricing" className="inline-flex items-center gap-2 text-sm text-[#5c534a] hover:text-[#1c1917]">
          <ArrowLeft className="h-4 w-4" />
          Back to pricing
        </Link>

        <section className="mt-5 rounded-2xl border border-[#e8e2d8] bg-[#fffcf7]/90 p-5 shadow-sm sm:p-6">
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">Checkout setup</p>
          <div className="mt-3 flex items-start gap-3">
            {planType === 'legacy_book' ? (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#e8e2d8] bg-[#faf8f5] text-[#8b7355]">
                <BookOpen className="h-5 w-5" aria-hidden />
              </span>
            ) : null}
            <div>
              <h1 className="font-serif text-2xl font-normal sm:text-3xl">{planCopy.title}</h1>
              <p className="mt-1 text-sm font-medium text-[#8b7355]">{planCopy.price}</p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-[#5c534a]">{planCopy.description}</p>
          {planType === 'legacy_book' ? (
            <p className="mt-2 text-xs leading-relaxed text-[#6c6258]">
              One-time payment for one archive. Not a monthly subscription. Family Archive subscription coming later.
            </p>
          ) : null}

          <div className="mt-5 rounded-xl border border-[#e8e2d8] bg-[#faf8f5]/70 p-4 text-sm text-[#4a433c]">
            <p className="font-medium text-[#1c1917]">Current plan: {billingState?.current_plan || '...'}</p>
            <p className="mt-1">{planCopy.upgradeNote}</p>
          </div>

          {needsTree && (
            <div className="mt-5">
              <label htmlFor="purchase-tree" className="mb-1.5 block text-sm font-medium text-[#3d3833]">
                Choose archive for this purchase
              </label>
              <select
                id="purchase-tree"
                value={selectedTreeId}
                onChange={(e) => setSelectedTreeId(e.target.value)}
                className="h-11 w-full rounded-md border border-[#e8e2d8] bg-white/90 px-3 text-sm"
              >
                {trees.map((tree) => (
                  <option key={tree.id} value={tree.id}>
                    {tree.name}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-[#68645e]">
                {planType === 'legacy_book'
                  ? 'Legacy Book export applies to the archive you select.'
                  : 'One purchase unlocks one family archive (tree).'}
              </p>
            </div>
          )}

          {blockedReason && (
            <p className="mt-4 rounded-xl border border-[#c9a961]/35 bg-[#c9a961]/10 px-3 py-2 text-sm text-[#5c4a32]">
              {blockedReason}
            </p>
          )}

          <Button
            onClick={startCheckout}
            disabled={loading || busy || !billingState || !canStartCheckout}
            className="mt-6 h-12 w-full rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824]"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {busy ? 'Processing...' : planCopy.cta}
          </Button>
        </section>
      </main>
    </div>
  );
};

export default BillingCheckout;
